import React, { useEffect, useState, useCallback } from "react";
import { useT } from "@/i18n";
import {
  readStoredConsent,
  writeStoredConsent,
  updateConsent,
  buildConsentFromCategories,
  ALL_GRANTED,
  DEFAULT_DENIED,
} from "@/lib/consent";
import { X } from "lucide-react";

export default function CookieBanner({ openPrefs, setOpenPrefs }) {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false, personalization: false });

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPrefs({
        analytics: stored.analytics_storage === "granted",
        ads: stored.ad_storage === "granted",
        personalization: stored.personalization_storage === "granted",
      });
    }
    const onStorage = (e) => {
      if (e.key === "zr_cookie_consent_v2" && !e.newValue) {
        setVisible(true);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (openPrefs) {
      setVisible(true);
      setShowManage(true);
    }
  }, [openPrefs]);

  const closeAll = useCallback(() => {
    setVisible(false);
    setShowManage(false);
    if (setOpenPrefs) setOpenPrefs(false);
  }, [setOpenPrefs]);

  const onAcceptAll = () => {
    const consent = { ...ALL_GRANTED };
    writeStoredConsent(consent);
    updateConsent(consent);
    setPrefs({ analytics: true, ads: true, personalization: true });
    closeAll();
  };
  const onRejectAll = () => {
    const consent = { ...DEFAULT_DENIED };
    writeStoredConsent(consent);
    updateConsent(consent);
    setPrefs({ analytics: false, ads: false, personalization: false });
    closeAll();
  };
  const onSave = () => {
    const consent = buildConsentFromCategories(prefs);
    writeStoredConsent(consent);
    updateConsent(consent);
    closeAll();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="cookie-banner-root">
      <div className="absolute inset-0 bg-black/40 pointer-events-auto" onClick={closeAll} aria-hidden="true" />
      <div
        className="pointer-events-auto absolute bottom-4 left-4 right-4 md:left-6 md:right-6 mx-auto max-w-[880px] rounded-2xl bg-[#0f141c] border border-white/10 shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-title"
        data-testid="cookie-banner"
      >
        <button
          type="button"
          onClick={closeAll}
          aria-label={t("cookies.back")}
          className="absolute top-3 right-3 rounded-full w-8 h-8 inline-flex items-center justify-center text-[#c8cdd6] hover:text-white hover:bg-white/5"
          data-testid="cookie-close"
        >
          <X size={16} />
        </button>

        {!showManage ? (
          <div className="p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9F14B]">Zenithradiar</div>
            <h2 id="cookie-title" className="mt-2 text-xl md:text-2xl font-semibold text-[#F1ECDE]">
              {t("cookies.bannerTitle")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-[#c8cdd6] max-w-2xl">
              {t("cookies.bannerBody")}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                onClick={onAcceptAll}
                data-testid="cookie-accept-all"
                className="rounded-full bg-[#C9F14B] text-[#0B0F14] px-5 py-2.5 text-sm font-medium hover:bg-[#d7ff5f] transition-colors"
              >
                {t("cookies.acceptAll")}
              </button>
              <button
                type="button"
                onClick={onRejectAll}
                data-testid="cookie-reject-all"
                className="rounded-full bg-white/5 border border-white/10 text-[#F1ECDE] px-5 py-2.5 text-sm font-medium hover:bg-white/10 transition-colors"
              >
                {t("cookies.rejectAll")}
              </button>
              <button
                type="button"
                onClick={() => setShowManage(true)}
                data-testid="cookie-manage"
                className="text-sm text-[#c8cdd6] underline underline-offset-4 hover:text-white sm:ml-auto"
              >
                {t("cookies.manage")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9F14B]">Zenithradiar</div>
            <h2 className="mt-2 text-xl md:text-2xl font-semibold text-[#F1ECDE]">
              {t("cookies.prefsTitle")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-[#c8cdd6] max-w-2xl">
              {t("cookies.prefsBody")}
            </p>
            <div className="mt-6 space-y-3">
              <CategoryRow
                title={t("cookies.categoryNecessaryTitle")}
                body={t("cookies.categoryNecessaryBody")}
                required
                requiredLabel={t("cookies.required")}
              />
              <CategoryRow
                testId="cookie-cat-analytics"
                title={t("cookies.categoryAnalyticsTitle")}
                body={t("cookies.categoryAnalyticsBody")}
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <CategoryRow
                testId="cookie-cat-ads"
                title={t("cookies.categoryAdsTitle")}
                body={t("cookies.categoryAdsBody")}
                checked={prefs.ads}
                onChange={(v) => setPrefs((p) => ({ ...p, ads: v }))}
              />
              <CategoryRow
                testId="cookie-cat-perso"
                title={t("cookies.categoryPersonalizationTitle")}
                body={t("cookies.categoryPersonalizationBody")}
                checked={prefs.personalization}
                onChange={(v) => setPrefs((p) => ({ ...p, personalization: v }))}
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                onClick={() => setShowManage(false)}
                className="rounded-full bg-transparent border border-white/10 text-[#F1ECDE] px-5 py-2.5 text-sm font-medium hover:bg-white/5 transition-colors"
                data-testid="cookie-back"
              >
                {t("cookies.back")}
              </button>
              <button
                type="button"
                onClick={onRejectAll}
                className="rounded-full bg-white/5 border border-white/10 text-[#F1ECDE] px-5 py-2.5 text-sm font-medium hover:bg-white/10 transition-colors"
              >
                {t("cookies.rejectAll")}
              </button>
              <button
                type="button"
                onClick={onSave}
                data-testid="cookie-save"
                className="sm:ml-auto rounded-full bg-[#C9F14B] text-[#0B0F14] px-5 py-2.5 text-sm font-medium hover:bg-[#d7ff5f] transition-colors"
              >
                {t("cookies.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ title, body, checked, onChange, required, requiredLabel, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-white/5 bg-white/[0.02] p-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#F1ECDE]">{title}</div>
        <div className="mt-1 text-xs text-[#8f96a3] leading-relaxed">{body}</div>
      </div>
      {required ? (
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#C9F14B] whitespace-nowrap">{requiredLabel}</span>
      ) : (
        <label className="relative inline-flex shrink-0 items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={testId}
          />
          <span className="w-10 h-6 bg-white/10 rounded-full peer-checked:bg-[#C9F14B] transition-colors relative">
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-[#0B0F14] shadow transition-transform ${
                checked ? "translate-x-4" : ""
              }`}
            />
          </span>
        </label>
      )}
    </div>
  );
}
